import React from 'react'
import PropTypes from 'prop-types'
import { Card, Collapse, Descriptions, Empty, Table, Tag, Typography } from 'antd'
import { formatDateTime } from './utils'

// Newest first, on a copy: `shipment.events` arrives oldest first and is also what other panels
// read, so sorting it in place would reorder somebody else's list.
const newestFirst = events =>
  [...(events || [])].sort((a, b) => String(b.at || '').localeCompare(String(a.at || '')))

/* eslint-disable react/display-name */
const eventColumns = [
  {
    title: 'When',
    key: 'when',
    render: (_, e) => formatDateTime(e.at)
  },
  {
    title: 'Event',
    key: 'code',
    render: (_, e) => (
      <span>
        <code>{e.code}</code>
        {e.estimated ? <Tag style={{ marginLeft: 4 }}>est.</Tag> : null}
      </span>
    )
  },
  {
    title: 'Where',
    key: 'where',
    render: (_, e) => {
      const location = e.location || {}
      if (!location.name && !location.locode) return '—'
      return location.locode ? `${location.name} (${location.locode})` : location.name
    }
  },
  {
    title: 'Vessel',
    key: 'vessel',
    render: (_, e) => (e.vessel ? `${e.vessel}${e.voyage ? ` / ${e.voyage}` : ''}` : '—')
  },
  {
    title: 'Container',
    key: 'container',
    render: (_, e) => e.container || '—'
  }
]
/* eslint-enable react/display-name */

// What ShipsGo actually said, in their words, including the parts nobody has mapped. When a
// shipment is wrong this is the panel that says why, so nothing here is prettified away.
const ShipsgoPanel = ({ shipment }) => {
  const row = shipment || {}
  const shipsgo = row.shipsgo || {}
  const previous = shipsgo.previousShipmentIds || []
  const detected = shipsgo.detectedCarrier || null
  const events = newestFirst(row.events)

  return (
    <Card title="ShipsGo">
      <Descriptions size="small" bordered column={2}>
        <Descriptions.Item label="ShipsGo shipment id">
          {shipsgo.shipmentId ? (
            <Typography.Text copyable>{shipsgo.shipmentId}</Typography.Text>
          ) : (
            <span style={{ opacity: 0.65 }}>Not created yet</span>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Raw status">
          {shipsgo.status ? <code>{shipsgo.status}</code> : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Carrier code sent">
          {shipsgo.carrierCode || '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Detected carrier">
          {detected ? `${detected.name || ''} ${detected.scac ? `(${detected.scac})` : ''}`.trim() : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Our reference sent">
          {shipsgo.reference || '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Created at">
          {formatDateTime(shipsgo.createdAt)}
        </Descriptions.Item>
        <Descriptions.Item label="Last synced">
          {formatDateTime(shipsgo.lastSyncAt)}
        </Descriptions.Item>
        <Descriptions.Item label="Discarded at">
          {formatDateTime(shipsgo.discardedAt)}
        </Descriptions.Item>
        <Descriptions.Item label="Previous ShipsGo ids" span={2}>
          {previous.length
            ? previous.map((id, index) => (
                <span key={id}>
                  <Typography.Text copyable>{id}</Typography.Text>
                  {index < previous.length - 1 ? ', ' : ''}
                </span>
              ))
            : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Start attempts">
          {shipsgo.startAttempts === null || shipsgo.startAttempts === undefined
            ? '—'
            : shipsgo.startAttempts}
        </Descriptions.Item>
        <Descriptions.Item label="Last start error">
          {shipsgo.startError ? (
            <Typography.Text type="danger">{shipsgo.startError}</Typography.Text>
          ) : (
            '—'
          )}
        </Descriptions.Item>
      </Descriptions>

      {/* Collapsed, because it is only ever read when the mapped fields do not explain what
          happened — but it is here, so nobody has to ask the server for it. */}
      <Collapse style={{ marginTop: 16 }}>
        <Collapse.Panel header="Raw ShipsGo data" key="raw">
          <pre style={{ maxHeight: 400, overflow: 'auto' }}>
            {JSON.stringify(shipsgo.raw || {}, null, 2)}
          </pre>
        </Collapse.Panel>
      </Collapse>

      <Table
        style={{ marginTop: 16 }}
        size="small"
        pagination={{ pageSize: 20 }}
        rowKey={(e, i) => `${e.at}-${e.code}-${i}`}
        dataSource={events}
        columns={eventColumns}
        scroll={{ x: 800 }}
        locale={{ emptyText: <Empty description="No events from ShipsGo yet." /> }}
      />
    </Card>
  )
}

ShipsgoPanel.propTypes = {
  shipment: PropTypes.object
}

export default ShipsgoPanel
